import React, { useState, useEffect, useRef } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { formatDateInTZ, daysLeftInTZ } from '../lib/localDate';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LinkedItemsPopover from '../components/LinkedItemsPopover';
import { toast } from 'sonner';
import { AdminTenderDialog } from '../components/dialogs/AdminTenderDialog';
import { usePlanGate, planGateFromResponse } from '../lib/planGate';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { buildCountryGate } from '../lib/countryGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import ManageLinksDialog from '../components/dialogs/ManageLinksDialog';
import EntityCardActions from '../components/EntityCardActions';
import useFavorites from '../hooks/useFavorites';
import { trackSearch, trackFeature } from '../services/eventTracker';
import { useOnboarding, OnboardingEmptyState, onboardingCopy } from '../onboarding';
import { STANDARD_INDUSTRIES, getIndustryLabel } from '../constants/industries';
import IndustryMultiSelectFilter from '../components/IndustryMultiSelectFilter';
import LinkedEntityFilter from '../components/LinkedEntityFilter';
import { 
  FileText, Search, Globe, Calendar, DollarSign, 
  ExternalLink, ChevronLeft, ChevronRight, LayoutGrid, List,
  Clock, Building, Building2, AlertCircle, CheckCircle2, RefreshCw,
  Filter, Download, ChevronDown, X, CalendarDays, Check, Rss,
  Info, AlertTriangle, Loader2, Database, Zap, Users, Briefcase, Plus, UserPlus, Factory, ArrowLeft, User, Link2, Lock, Heart, Bell, Newspaper
} from 'lucide-react';
import { SERVER_SOURCE_KEYS } from '../components/tenderSourceCatalog';
import { pathId, idFromPath } from '../lib/pathId';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Safely render a tender field that may be a localized {en, ar} object.
// Rendering such an object directly crashes React ("Objects are not valid as
// a React child") — this was the root cause of the delete-tender black screen.
const pickTenderText = (v) => {
  if (v == null) return '';
  if (typeof v === 'object') return v.en || v.ar || '';
  return v;
};


const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

const statusColors = {
  open: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  under_review: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  closed: 'bg-gray-500/10 text-gray-500 border-gray-500/30',
  expired: 'bg-red-500/10 text-red-500 border-red-500/30',
  awarded: 'bg-blue-500/10 text-blue-500 border-blue-500/30'
};

const statusIcons = {
  open: CheckCircle2,
  under_review: CheckCircle2,
  closed: AlertCircle,
  expired: AlertCircle,
  awarded: CheckCircle2
};

// Official Portal URLs for GCC countries
const PORTAL_URLS = {
  SA: [
    { name: 'Etimad', url: 'https://portal.etimad.sa/en-us', description: 'Government Procurement' },
    { name: 'Muqawil', url: 'https://www.muqawil.org/en/market/list', description: 'Contractor Marketplace' }
  ],
  AE: [
    { name: 'Dubai eSupply', url: 'https://esupply.dubai.gov.ae', description: 'Dubai Government' },
    { name: 'Abu Dhabi ADGPG', url: 'https://www.adgpg.gov.ae/en/For-Suppliers/Public-Tenders', description: 'Abu Dhabi Procurement' },
    { name: 'Sharjah eProcurement', url: 'https://www.eprocurement.sfd.gov.ae', description: 'Sharjah Finance Dept' },
    { name: 'Ajman Tenders', url: 'https://digitalajman.ae/en/tenders', description: 'Ajman Digital' },
    { name: 'RAK Tenders', url: 'https://www.rak.ae', description: 'Ras Al Khaimah' },
    { name: 'Fujairah Portal', url: 'https://fujairah.ae/en', description: 'Fujairah Government' },
    { name: 'UAQ Portal', url: 'https://sgd.uaq.ae', description: 'Umm Al Quwain' }
  ],
  OM: [
    { name: 'Oman Tender Board', url: 'https://etendering.tenderboard.gov.om', description: 'Government Tenders' }
  ],
  KW: [
    { name: 'CAPT Kuwait', url: 'https://capt.gov.kw/en/', description: 'Central Agency' }
  ],
  BH: [
    { name: 'Bahrain Tender Board', url: 'https://etendering.tenderboard.gov.bh', description: 'Government Tenders' }
  ],
  QA: [
    { name: 'Ashghal', url: 'https://www.ashghal.gov.qa/en/Tenders/Pages/default.aspx', description: 'Public Works' }
  ]
};

// Date presets
const datePresets = [
  { id: 'any', en: 'Any Date', ar: 'أي تاريخ', days: null },
  { id: 'today', en: 'Today', ar: 'اليوم', days: 0 },
  { id: 'week', en: 'Past Week', ar: 'الأسبوع الماضي', days: 7 },
  { id: 'month', en: 'Past Month', ar: 'الشهر الماضي', days: 30 },
  { id: '3months', en: 'Past 3 Months', ar: 'آخر 3 أشهر', days: 90 },
  { id: '6months', en: 'Past 6 Months', ar: 'آخر 6 أشهر', days: 180 },
  { id: 'older', en: 'More than 6 Months', ar: 'أكثر من 6 أشهر', days: -180 },
  { id: 'custom', en: 'Custom Range', ar: 'نطاق مخصص', days: 'custom' }
];

// A DEADLINE is in the future; ``datePresets`` above is entirely backward-looking,
// so reusing it for the deadline filter can only ever select tenders that have
// already closed. These are the forward-looking equivalents.
const deadlinePresets = [
  { id: 'any',          en: 'Any Date',       ar: 'أي تاريخ',              ahead: undefined },
  { id: 'closing-today',en: 'Closing Today',  ar: 'يُغلق اليوم',            ahead: 0 },
  { id: 'next-week',    en: 'Next 7 Days',    ar: 'الأيام السبعة القادمة',  ahead: 7 },
  { id: 'next-month',   en: 'Next 30 Days',   ar: 'الثلاثون يوماً القادمة', ahead: 30 },
  { id: 'next-3months', en: 'Next 3 Months',  ar: 'الأشهر الثلاثة القادمة', ahead: 90 },
  { id: 'closed',       en: 'Already Closed', ar: 'مغلقة بالفعل',          ahead: -1 },
  { id: 'custom',       en: 'Custom Range',   ar: 'نطاق مخصص',             ahead: 'custom' }
];

// WO-32 — the sources Mid2Bid actually collects (3 by the server, 8 by the admin's
// Collector add-on), plus the old labels still carried by stored records until the
// clean-up runs. `value` is matched as a substring of tenders.source.
const ALL_TENDER_SOURCES = [
  { value: 'Etimad', label: 'Etimad', country: 'SA' },
  { value: 'Muqawil', label: 'Muqawil (private projects)', country: 'SA' },
  { value: 'Abu Dhabi ADGPG', label: 'Abu Dhabi ADGPG', country: 'AE' },
  { value: 'Dubai eSupply', label: 'Dubai eSupply', country: 'AE' },
  { value: 'Sharjah eProcurement', label: 'Sharjah eProcurement', country: 'AE' },
  { value: 'UAE Federal Procurement', label: 'UAE Federal Procurement', country: 'AE' },
  { value: 'Ashghal', label: 'Qatar Ashghal', country: 'QA' },
  { value: 'Monaqasat', label: 'Qatar Monaqasat', country: 'QA' },
  { value: 'Bahrain Tender Board', label: 'Bahrain Tender Board', country: 'BH' },
  { value: 'Kuwait CAPT', label: 'Kuwait CAPT', country: 'KW' },
  { value: 'Oman Tender Board', label: 'Oman Tender Board', country: 'OM' }
];

// Date Filter Component
const DateFilter = ({ label, selectedPreset, onPresetChange, dateRange, onDateRangeChange, language, isRTL, testId, allowFuture = false, presets = datePresets }) => {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(selectedPreset === 'custom');
  const currentYear = new Date().getFullYear();

  // Month-year picker state
  const [viewYear, setViewYear] = useState(currentYear);
  const [editingYear, setEditingYear] = useState(false);
  const [yearInputVal, setYearInputVal] = useState(String(currentYear));
  const [localFrom, setLocalFrom] = useState('');
  const [localTo, setLocalTo] = useState('');
  const [pickStep, setPickStep] = useState('from');

  const ymFromDate = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : '';

  const MONTHS = language === 'ar'
    ? ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر']
    : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const syncFromDateRange = () => {
    if (dateRange?.from) {
      const fy = ymFromDate(dateRange.from);
      const ty = ymFromDate(dateRange.to);
      setLocalFrom(fy);
      setLocalTo(ty || '');
      setViewYear(dateRange.from.getFullYear());
    } else {
      setLocalFrom('');
      setLocalTo('');
    }
    setPickStep('from');
    setEditingYear(false);
  };

  const handleOpenChange = (isOpen) => {
    if (isOpen && selectedPreset === 'custom') syncFromDateRange();
    setOpen(isOpen);
  };

  const handlePresetSelect = (preset) => {
    onPresetChange(preset.id);
    if (preset.id === 'custom') {
      setShowCustom(true);
      syncFromDateRange();
    } else {
      setShowCustom(false);
      onDateRangeChange({ from: null, to: null });
      setOpen(false);
    }
  };

  const handleMonthClick = (monthIdx) => {
    const ym = `${viewYear}-${String(monthIdx + 1).padStart(2, '0')}`;
    if (pickStep === 'from') {
      setLocalFrom(ym);
      setLocalTo('');
      setPickStep('to');
    } else {
      const [finalFrom, finalTo] = ym >= localFrom ? [localFrom, ym] : [ym, localFrom];
      setLocalFrom(finalFrom);
      setLocalTo(finalTo);
      const [fy, fm] = finalFrom.split('-').map(Number);
      const [ty, tm] = finalTo.split('-').map(Number);
      onDateRangeChange({
        from: new Date(fy, fm - 1, 1),
        to: new Date(ty, tm, 0) // last day of "to" month
      });
      setOpen(false);
    }
  };

  const getMonthState = (monthIdx) => {
    const ym = `${viewYear}-${String(monthIdx + 1).padStart(2, '0')}`;
    if (ym === localFrom) return 'from';
    if (ym === localTo) return 'to';
    if (localFrom && localTo && ym > localFrom && ym < localTo) return 'range';
    return 'none';
  };

  const commitYear = () => {
    const y = parseInt(yearInputVal);
    if (y >= 2000 && y <= 2060) setViewYear(y);
    setEditingYear(false);
  };

  const formatDateLabel = () => {
    const baseLabel = label ? `${label}: ` : '';
    if (selectedPreset === 'custom' && dateRange?.from) {
      const fromStr = dateRange.from.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', year: 'numeric' });
      const toStr = dateRange.to
        ? dateRange.to.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', year: 'numeric' })
        : language === 'ar' ? 'الآن' : 'Now';
      return `${baseLabel}${fromStr} - ${toStr}`;
    }
    const preset = presets.find(p => p.id === selectedPreset);
    const presetLabel = preset ? (language === 'ar' ? preset.ar : preset.en) : (language === 'ar' ? 'أي تاريخ' : 'Any Date');
    return `${baseLabel}${presetLabel}`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-11 px-3 justify-between min-w-[200px]"
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[180px]">
              {formatDateLabel()}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Quick Select presets */}
          <div className="border-r">
            <div className="p-2 border-b">
              <span className="text-xs font-medium text-muted-foreground px-2">
                {label || (language === 'ar' ? 'اختيار سريع' : 'Quick Select')}
              </span>
            </div>
            <div className="p-1">
              {presets.map((preset) => (
                <div
                  key={preset.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                    selectedPreset === preset.id ? 'bg-primary/10 text-primary' : ''
                  }`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {selectedPreset === preset.id && <Check className="w-4 h-4" />}
                  <span className={`text-sm ${selectedPreset === preset.id ? 'font-medium' : ''}`}>
                    {language === 'ar' ? preset.ar : preset.en}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Range — month-year grid with direct year editing */}
          {showCustom && (
            <div className="p-4 w-60">
              <p className="text-xs text-muted-foreground mb-3 font-medium">
                {pickStep === 'from'
                  ? (language === 'ar' ? 'اختر شهر البداية' : 'Select start month')
                  : (language === 'ar' ? 'اختر شهر النهاية' : 'Select end month')}
                {localFrom && pickStep === 'to' && (
                  <span className="ml-1 font-semibold text-primary">{localFrom}</span>
                )}
              </p>

              {/* Year nav — click year to type directly */}
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => setViewYear(y => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {editingYear ? (
                  <input
                    type="number"
                    value={yearInputVal}
                    onChange={e => setYearInputVal(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === 'Tab') commitYear();
                      if (e.key === 'Escape') setEditingYear(false);
                    }}
                    onBlur={commitYear}
                    className="w-20 text-center font-bold text-base border-b-2 border-primary bg-transparent focus:outline-none"
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => { setYearInputVal(String(viewYear)); setEditingYear(true); }}
                    className="font-bold text-base hover:text-primary transition-colors px-2 rounded"
                    title={language === 'ar' ? 'انقر لكتابة السنة مباشرة' : 'Click to type year directly'}
                  >
                    {viewYear}
                  </button>
                )}
                <button onClick={() => setViewYear(y => y + 1)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Month grid 4×3 */}
              <div className="grid grid-cols-4 gap-1">
                {MONTHS.map((m, idx) => {
                  const state = getMonthState(idx);
                  return (
                    <button
                      key={idx}
                      onClick={() => handleMonthClick(idx)}
                      className={`py-2 text-xs rounded-md font-medium transition-colors ${
                        state === 'from' || state === 'to'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : state === 'range'
                            ? 'bg-primary/15 text-primary'
                            : 'hover:bg-muted text-foreground'
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>

              {/* Summary + clear */}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {localFrom ? `${localFrom} → ${localTo || '…'}` : (language === 'ar' ? 'لم يُحدَّد' : 'Not set')}
                </span>
                <Button
                  size="sm" variant="ghost"
                  className="h-7 text-xs text-muted-foreground hover:text-destructive px-2"
                  onClick={() => {
                    setLocalFrom(''); setLocalTo(''); setPickStep('from');
                    setShowCustom(false);
                    onPresetChange('any');
                    onDateRangeChange({ from: null, to: null });
                  }}
                >
                  {language === 'ar' ? 'مسح' : 'Clear'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Multi-select filter component
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, language, isRTL, testId, onLockedClick }) => {
  const [open, setOpen] = useState(false);
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = () => onChange([]);
  const selectAll = () => onChange(options.map(o => o.value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-11 px-3 justify-between min-w-[160px]"
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[100px]">
              {selected.length === 0 
                ? label 
                : selected.length === 1 
                  ? options.find(o => o.value === selected[0])?.label || selected[0]
                  : `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`
              }
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="p-2 border-b flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">
            {language === 'ar' ? 'تحديد الكل' : 'Select All'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
            {language === 'ar' ? 'مسح' : 'Clear'}
          </Button>
        </div>
        <ScrollArea className="h-[250px]">
          <div className="p-2 space-y-1">
            {options.map((option) => (
              <div
                key={option.value}
                className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                  option.locked ? 'opacity-70' : selected.includes(option.value) ? 'bg-primary/10' : ''
                }`}
                onClick={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
                data-testid={option.comingSoon ? `multiselect-coming-soon-${option.value}` : undefined}
              >
                <Checkbox
                  checked={!option.locked && selected.includes(option.value)}
                  onCheckedChange={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
                  disabled={option.locked}
                  className="pointer-events-none"
                />
                {option.icon && <span>{option.icon}</span>}
                <span className="text-sm flex-1 truncate">{option.label}</span>
                {option.locked && (
                  <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 whitespace-nowrap" data-testid={`locked-country-${option.value}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    {language === 'ar' ? 'ستاندرد' : 'Standard'}
                  </span>
                )}
                {option.comingSoon && (
                  <span
                    className="ml-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30 whitespace-nowrap"
                    title={language === 'ar'
                      ? 'هذا المصدر غير متاح حاليًا. سنقوم بتفعيله في مرحلة لاحقة.'
                      : 'This source is not available yet — we will enable it in a later stage.'}
                  >
                    {language === 'ar' ? 'قريبًا' : 'Coming Soon'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        {selected.length > 0 && (
          <div className="p-2 border-t">
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((val) => {
                const opt = options.find(o => o.value === val);
                return (
                  <Badge key={val} variant="secondary" className="text-xs">
                    {opt?.icon} {opt?.label?.slice(0, 10)}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={(e) => { e.stopPropagation(); toggleOption(val); }}
                    />
                  </Badge>
                );
              })}
              {selected.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{selected.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default function TendersPage() {
  const { language, isRTL } = useLanguage();
  const { user, token } = useAuth();
  const planGate = usePlanGate();
  const { allowedCountries, allCountries, upgradeUrl, planId } = useEntitlements();
  const isCountryLocked = (code) => !allCountries && !allowedCountries.includes(code);
  const handleLockedCountry = (code) => planGate.show(buildCountryGate(code, planId, upgradeUrl, language));
  const [countryGate, setCountryGate] = useState(null);
  const navigate = useNavigate();
  // WO-35: the address carries "∕" where the id has "/" (lib/pathId.js).
  const { id: deepLinkParam } = useParams();
  const deepLinkId = idFromPath(deepLinkParam);
  const [searchParams] = useSearchParams();
  const deepLinkRef = useRef(null);

  // WO-33 — "Add to CRM" on a tender card (shown while onboarding is on). The
  // route /opportunities/from-tender existed but no screen called it, so a
  // member had no way to turn a tender into an opportunity.
  const onb = useOnboarding();
  const [crmSavingId, setCrmSavingId] = useState(null);
  const [crmSavedIds, setCrmSavedIds] = useState(() => new Set());
  const handleAddTenderToCrm = async (tender) => {
    const txtCrm = onboardingCopy.en.addToCrm;
    setCrmSavingId(tender._id);
    try {
      const response = await fetch(`${BACKEND_URL}/api/opportunities/from-tender/${pathId(tender._id)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : txtCrm.failed);
      setCrmSavedIds(prev => new Set(prev).add(tender._id));
      if (data.already_in_crm) {
        toast.info(txtCrm.already);
      } else {
        toast.success(txtCrm.added);
        trackFeature('extract_lead', { entity: 'tender' });
      }
      onb.refresh();
    } catch (error) {
      console.error('Error adding tender to CRM:', error);
      toast.error(error.message || txtCrm.failed);
    } finally {
      setCrmSavingId(null);
    }
  };

  // BUG-L02 FIX: scroll to the deep-linked tender after list loads
  useEffect(() => {
    if (!deepLinkId) return;
    setTimeout(() => {
      if (deepLinkRef.current) {
        deepLinkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 400);
  }, [deepLinkId, deepLinkRef.current]);
  const filterIds = (searchParams.get('filter_ids') || '').split(',').filter(Boolean);
  const isFiltered = filterIds.length > 0;
  const [tenders, setTenders] = useState([]);
  const [allSources, setAllSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('card');
  const [scraperStatus, setScraperStatus] = useState(null);
  const [scraping, setScraping] = useState({});
  const [activeTenderJob, setActiveTenderJob] = useState(null);
  const [retrievalOpen, setRetrievalOpen] = useState(false);
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  const userPlan = user?.plan_id || 'free';
  const isPremiumOrEnterprise = userPlan === 'premium' || userPlan === 'enterprise';
  
  // Admin dialog state
  const [showAdminTenderDialog, setShowAdminTenderDialog] = useState(false);
  const [editingTender, setEditingTender] = useState(null);
  const [deletingTender, setDeletingTender] = useState({});
  const [tenderToDelete, setTenderToDelete] = useState(null);
  
  // Lead extraction state
  const [extractingLeads, setExtractingLeads] = useState({});
  const [showLeadsDialog, setShowLeadsDialog] = useState(false);
  const [currentLeads, setCurrentLeads] = useState([]);
  const [currentTenderForLeads, setCurrentTenderForLeads] = useState(null);
  const [savingLead, setSavingLead] = useState({});
  
  // Custom lead dialog state
  const [showCustomLeadDialog, setShowCustomLeadDialog] = useState(false);
  const [customLeadType, setCustomLeadType] = useState('project');
  const [customLeadName, setCustomLeadName] = useState('');
  const [customLeadDescription, setCustomLeadDescription] = useState('');
  const [creatingCustomLead, setCreatingCustomLead] = useState(false);
  
  // Multi-select Filters
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [linkedProjectFilter, setLinkedProjectFilter] = useState([]);
  const [linkedCompanyFilter, setLinkedCompanyFilter] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  
  // Date Filters
  // 1) Published Date filter (renamed from previous single date filter)
  const [datePreset, setDatePreset] = useState('any');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  // 2) Deadline Date filter (new)
  const [deadlinePreset, setDeadlinePreset] = useState('any');
  const [deadlineRange, setDeadlineRange] = useState({ from: null, to: null });
  
  // Use the centralized favorites hook
  const { 
    isFavorite, 
    handleFavoriteToggle, 
    filterByFavorites,
    showFavoritesOnly, 
    setShowFavoritesOnly 
  } = useFavorites('tender', token);
  
  // Create Alert from filters state
  const [showCreateAlertDialog, setShowCreateAlertDialog] = useState(false);
  const [alertName, setAlertName] = useState('');
  const [alertFrequency, setAlertFrequency] = useState('realtime');
  const [alertChannels, setAlertChannels] = useState(['in_app', 'email']);
  const [savingAlert, setSavingAlert] = useState(false);
  
  // Page size
  const PAGE_SIZE = 20;

  // Calculate date filter bounds for any (preset, range) pair
  const computeDateBounds = (preset, range) => {
    if (preset === 'custom' && range && range.from) {
      return { from: range.from, to: range.to || new Date() };
    }
    const presetCfg = datePresets.find(p => p.id === preset);
    if (!presetCfg || presetCfg.days === null) return null;
    const now = new Date();
    if (presetCfg.days === -180) {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setDate(sixMonthsAgo.getDate() - 180);
      return { from: null, to: sixMonthsAgo };
    } else if (presetCfg.days === 0) {
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { from: startOfDay, to: now };
    } else {
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - presetCfg.days);
      return { from: pastDate, to: now };
    }
  };

  // A deadline is in the FUTURE, so it needs its own bounds. The upper bound is the
  // END of the chosen local day, so a tender closing during that day is included
  // rather than excluded.
  const endOfLocalDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  const computeDeadlineBounds = (preset, range) => {
    if (preset === 'custom') {
      if (!range || !range.from) return null;
      return { from: range.from, to: range.to ? endOfLocalDay(range.to) : null };
    }
    const cfg = deadlinePresets.find(p => p.id === preset);
    if (!cfg || cfg.ahead === undefined || cfg.ahead === 'custom') return null;
    const now = new Date();
    if (cfg.ahead === -1) return { from: null, to: now };
    if (cfg.ahead === 0) return { from: now, to: endOfLocalDay(now) };
    const ahead = new Date();
    ahead.setDate(ahead.getDate() + cfg.ahead);
    return { from: now, to: endOfLocalDay(ahead) };
  };

  const getDateBounds = () => computeDateBounds(datePreset, dateRange);
  const getDeadlineBounds = () => computeDeadlineBounds(deadlinePreset, deadlineRange);

  const fetchTenders = async (isLoadMore = false, forceRefresh = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const currentPage = isLoadMore ? page + 1 : 1;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: PAGE_SIZE.toString()
      });
      
      // Add cache-busting parameter for refresh
      if (forceRefresh) {
        params.append('_t', Date.now().toString());
      }
      
      // Server-side filters (multi-value: repeat the query param)
      selectedCountries.forEach(c => params.append('country', c));
      selectedStatuses.forEach(s => params.append('status', s));
      selectedIndustries.forEach(i => params.append('industry', i));
      selectedSources.forEach(s => params.append('source', s));
      if (search) params.append('search', search);
      if (linkedProjectFilter.length > 0) linkedProjectFilter.forEach(f => params.append('project_id', f._id));
      if (linkedCompanyFilter.length > 0) linkedCompanyFilter.forEach(f => params.append('company_id', f._id));
      
      // Date filters
      const dateBounds = getDateBounds();
      if (dateBounds && dateBounds.from) {
        params.append('date_from', dateBounds.from.toISOString());
      }
      if (dateBounds && dateBounds.to) {
        params.append('date_to', dateBounds.to.toISOString());
      }
      const deadlineBounds = getDeadlineBounds();
      if (deadlineBounds && deadlineBounds.from) {
        params.append('deadline_from', deadlineBounds.from.toISOString());
      }
      if (deadlineBounds && deadlineBounds.to) {
        params.append('deadline_to', deadlineBounds.to.toISOString());
      }
      
      // Identify the caller so the country rule applies (Batch F1 fix — this
      // list call previously sent no token, so the backend saw an anonymous
      // request and returned all six markets regardless of plan).
      const response = await fetch(`${BACKEND_URL}/api/intelligence/tenders?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch tenders');
      
      const data = await response.json();
      
      // Collect all unique sources from raw data (before filtering)
      const sources = [...new Set(data.items.map(item => item.source).filter(Boolean))];
      setAllSources(prev => {
        const combined = [...new Set([...prev, ...sources])];
        return combined.length > prev.length ? combined : prev;
      });
      
      // All multi-select filters are applied server-side (see fetch params),
      // so pagination and the "showing X of Y" counts stay accurate across pages.
      const items = data.items;

      if (isLoadMore) {
        setTenders(prev => [...prev, ...items]);
        setPage(currentPage);
      } else {
        setTenders(items);
        setPage(1);
      }
      
      setHasMore(data.page < data.total_pages);
      setTotal(data.total);
      setCountryGate(data.plan_gate || null);
      
      // Show success toast only on explicit manual refresh (not on initial load,
      // filter changes, or load-more actions) — avoids duplicate toasts (P2-06).
      if (!isLoadMore && forceRefresh) {
        toast.success(language === 'ar' ? 'تم تحديث المناقصات' : 'Tenders refreshed');
      }
    } catch (err) {
      setError(err.message);
      if (forceRefresh) {
        toast.error(language === 'ar' ? 'فشل تحديث المناقصات' : 'Failed to refresh tenders');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMore = () => {
    fetchTenders(true);
  };

  // Filter tenders by favorites if toggle is on
  const displayedTenders = filterByFavorites(tenders);

  const [scraperHealth, setScraperHealth] = useState(null);

  const fetchScraperStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/scraper-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScraperStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch scraper status:', err);
    }
  };

  const fetchScraperHealth = async () => {
    if (!isAdmin) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/tenders/scrapers/health`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setScraperHealth(data);
      }
    } catch (err) {
      console.error('Failed to fetch scraper health:', err);
    }
  };

  // WO-32 — the Retrieval menu starts ONE job for the three server sources.
  // (It used to start seven jobs two seconds apart; the server allows one job at a
  // time, so only the first source ever ran while the screen said all had started.)
  const SCHEDULER_KEY_FROM_STATUS = { server: SERVER_SOURCE_KEYS.join(',') };

  const triggerScrape = async (source) => {
    // If a job is already running, surface a friendly notice — backend will
    // return the existing job_id anyway and we just attach to it.
    if (activeTenderJob && (activeTenderJob.status === 'running' || activeTenderJob.status === 'queued')) {
      toast.info(language === 'ar'
        ? 'تحديث المناقصات قيد التشغيل بالفعل في الخلفية'
        : 'A tender refresh is already running in the background');
      return;
    }
    setScraping(prev => ({ ...prev, [source]: true }));
    try {
      const key = SCHEDULER_KEY_FROM_STATUS[source] || source;
      const response = await fetch(`${BACKEND_URL}/api/intelligence/tenders/ingest-start?source_keys=${encodeURIComponent(key)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Failed to start scraping');
        setScraping(prev => ({ ...prev, [source]: false }));
        return;
      }
      const jobId = data.job_id;
      toast.success(
        language === 'ar'
          ? `بدأ جلب البيانات في الخلفية`
          : `Scrape started in background — you can leave this page`,
        {
          description: language === 'ar'
            ? `سيستمر التحديث حتى الانتهاء (job ${String(jobId).slice(0,8)}…)`
            : `Worker will keep running until completion (job ${String(jobId).slice(0,8)}…)`,
        }
      );
      // Subscribe to live progress (closing this just unsubscribes — worker keeps going).
      streamTenderJob(jobId, source);
    } catch (err) {
      toast.error('Failed to trigger scraping');
      setScraping(prev => ({ ...prev, [source]: false }));
    }
  };

  const streamTenderJob = async (jobId, originSource = null) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/tenders/ingest-job/${jobId}/stream`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' },
      });
      if (!response.ok) throw new Error('Failed to attach to tender job stream');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.event === 'progress') {
              setActiveTenderJob({
                _id: data.job_id,
                status: data.status,
                current_source: data.current_source,
                total_sources: data.total_sources,
                totals: data.totals,
              });
            } else if (data.event === 'complete') {
              const t = data.totals || {};
              setActiveTenderJob({ _id: data.job_id, status: data.status, totals: t, per_source: data.per_source });
              const created = t.created || 0;
              if (created > 0) {
                toast.success(
                  language === 'ar' ? `تم جلب ${created} مناقصة جديدة` : `Fetched ${created} new tenders`,
                  { description: language === 'ar'
                      ? `${t.translated || 0} مترجم • ${t.filtered || 0} مستبعد قبل التاريخ المحدد`
                      : `${t.translated || 0} translated • ${t.filtered || 0} filtered (cut-off / cursor)` }
                );
              } else {
                toast.info(language === 'ar' ? 'لا توجد مناقصات جديدة' : 'No new tenders since last run');
              }
              await fetchTenders(false, true);
              await fetchScraperStatus();
              await fetchScraperHealth();
              if (originSource) setScraping(prev => ({ ...prev, [originSource]: false }));
              return;
            } else if (data.event === 'error') {
              throw new Error(data.message);
            }
          } catch {
            /* ignore malformed sse line */
          }
        }
      }
    } catch (err) {
      console.error('Tender job stream error:', err);
      toast.error(language === 'ar' ? 'انقطعت متابعة التحديث' : 'Lost progress connection (worker keeps running)');
    } finally {
      if (originSource) setScraping(prev => ({ ...prev, [originSource]: false }));
    }
  };

  useEffect(() => {
    if (isFiltered) return;
    fetchTenders(false);
    fetchScraperStatus();
    fetchScraperHealth();
  }, [isFiltered, selectedCountries, selectedStatuses, selectedSources, selectedIndustries, search, datePreset, dateRange, deadlinePreset, deadlineRange]);

  useEffect(() => {
    if (!isFiltered || !token) return;
    const loadFiltered = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          filterIds.map(id =>
            fetch(`${BACKEND_URL}/api/intelligence/tenders/${pathId(id)}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.ok ? r.json() : null)
          )
        );
        const valid = results.filter(Boolean);
        setTenders(valid);
        setTotal(valid.length);
      } finally {
        setLoading(false);
      }
    };
    loadFiltered();
  }, [filterIds.join(','), token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reattach to any running tender-ingestion job so progress survives tab reloads.
  useEffect(() => {
    if (!isAdmin || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/api/intelligence/tenders/ingest-job/active`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const { job } = await resp.json();
        if (!cancelled && job && (job.status === 'running' || job.status === 'queued')) {
          setActiveTenderJob(job);
          toast.info(
            language === 'ar' ? 'تم العثور على تحديث جارٍ — جارٍ المتابعة' : 'Found an ongoing tender refresh — re-attaching',
            { description: `job ${String(job._id).slice(0, 8)}…` }
          );
          streamTenderJob(job._id);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedStatuses([]);
    setSelectedSources([]);
    setSelectedIndustries([]);
    setDatePreset('any');
    setDateRange({ from: null, to: null });
    setDeadlinePreset('any');
    setDeadlineRange({ from: null, to: null });
    setLinkedProjectFilter([]);
    setLinkedCompanyFilter([]);
  };

  const hasActiveFilters = selectedCountries.length > 0 || selectedStatuses.length > 0 || selectedSources.length > 0 || selectedIndustries.length > 0 || datePreset !== 'any' || deadlinePreset !== 'any' || linkedProjectFilter.length > 0 || linkedCompanyFilter.length > 0;

  // Create alert from current filters
  const handleCreateAlertFromFilters = async () => {
    if (!alertName.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم التنبيه' : 'Please enter an alert name');
      return;
    }
    
    // Check if user has paid plan
    if (user?.plan_id === 'free') {
      toast.error(language === 'ar' 
        ? 'التنبيهات غير متاحة في الخطة المجانية. يرجى الترقية.'
        : 'Alerts are not available on the Free plan. Please upgrade.');
      return;
    }
    
    setSavingAlert(true);
    try {
      const alertData = {
        name: alertName,
        filters: {
          countries: selectedCountries,
          industries: selectedIndustries.map(id => {
            const ind = STANDARD_INDUSTRIES.find(i => i.id === id);
            return ind ? ind.en : id;
          }),
          keywords: search ? [search] : [],
          min_value: null,
          max_value: null,
          sources: selectedSources
        },
        frequency: alertFrequency,
        channels: alertChannels,
        is_active: true
      };
      
      const response = await fetch(`${BACKEND_URL}/api/alerts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertData)
      });
      
      if (response.status === 403) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) { setSavingAlert(false); return; }
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Alerts not available on your plan');
        setSavingAlert(false);
        return;
      }
      if (response.status === 402) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) { setSavingAlert(false); return; }
      }
      
      if (!response.ok) {
        // WO-33 — show the server's reason (e.g. "Choose at least one market…").
        const errData = await response.json().catch(() => ({}));
        throw new Error(typeof errData.detail === 'string' ? errData.detail : 'Failed to create alert');
      }
      
      toast.success(language === 'ar' 
        ? 'تم إنشاء التنبيه بنجاح! ستتلقى إشعارات عند توفر مناقصات جديدة.'
        : 'Alert created successfully! You\'ll be notified when matching tenders are published.');
      
      setShowCreateAlertDialog(false);
      setAlertName('');
      setAlertFrequency('realtime');
      setAlertChannels(['in_app', 'email']);
      
      trackFeature('alert_created_from_tenders', { filters: alertData.filters });
    } catch (error) {
      console.error('Error creating alert:', error);
      toast.error(language === 'ar' ? 'فشل إنشاء التنبيه' : (error.message || 'Failed to create alert'));
    } finally {
      setSavingAlert(false);
    }
  };

  const openCreateAlertDialog = () => {
    // Generate a smart default name based on filters
    const nameParts = [];
    if (selectedCountries.length === 1) {
      nameParts.push(countryNames[language][selectedCountries[0]]);
    } else if (selectedCountries.length > 1) {
      nameParts.push(`${selectedCountries.length} ${language === 'ar' ? 'دول' : 'Countries'}`);
    }
    if (selectedIndustries.length === 1) {
      const ind = STANDARD_INDUSTRIES.find(i => i.id === selectedIndustries[0]);
      if (ind) nameParts.push(language === 'ar' ? ind.ar : ind.en);
    } else if (selectedIndustries.length > 1) {
      nameParts.push(`${selectedIndustries.length} ${language === 'ar' ? 'صناعات' : 'Industries'}`);
    }
    if (search) {
      nameParts.push(`"${search}"`);
    }
    
    const defaultName = nameParts.length > 0 
      ? `${nameParts.join(' ')} ${language === 'ar' ? 'مناقصات' : 'Tenders'}`
      : (language === 'ar' ? 'تنبيه المناقصات' : 'Tender Alert');
    
    setAlertName(defaultName);
    setShowCreateAlertDialog(true);
  };

  const formatCurrency = (value, currency) => {
    if (!value) return language === 'ar' ? 'غير محدد' : 'N/A';
    const formatted = new Intl.NumberFormat(language === 'ar' ? 'ar-SA' : 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
    return formatted;
  };

  const formatDeadline = (dateStr, tz) => {
    if (!dateStr) return { text: language === 'ar' ? 'غير محدد' : 'N/A', tier: 'none' };
    // WO-03 item 5 — days-left and the rendered date are BOTH computed in the
    // tender's own country timezone, so a Saudi deadline reads the same day for
    // a viewer in Riyadh and in Madrid. One long form, never a numeric date.
    const daysLeft = daysLeftInTZ(dateStr, tz);
    const dateFormatted = formatDateInTZ(dateStr, tz, language);
    if (daysLeft == null) return { text: dateFormatted, tier: 'none' };

    // Urgency tiers (DD-report P1):
    //   past (expired)  → grey    past: true
    //   ≤ 7  days left  → red     tier: 'red'
    //   ≤ 30 days left  → amber   tier: 'amber'
    //   > 30 days left  → green   tier: 'green'
    if (daysLeft < 0) return { text: dateFormatted, urgent: false, past: true, tier: 'past', daysLeft };
    if (daysLeft <= 7) return { text: dateFormatted, urgent: true, daysLeft, tier: 'red' };
    if (daysLeft <= 30) return { text: dateFormatted, urgent: false, daysLeft, tier: 'amber' };
    return { text: dateFormatted, urgent: false, daysLeft, tier: 'green' };
  };

  const translations = {
    en: {
      title: 'Tenders',
      subtitle: 'Live data from official GCC tender portals and Premium/Enterprise users',
      searchPlaceholder: 'Search tenders...',
      allCountries: 'All Countries',
      allStatuses: 'All Statuses',
      allSources: 'All Sources',
      open: 'Open',
      closed: 'Closed',
      awarded: 'Awarded',
      results: 'tenders found',
      noTenders: 'No tenders available',
      noTendersDesc: 'Click "Fetch from Portal" to scrape live data from official sources',
      viewDetails: 'View on Portal',
      deadline: 'Deadline',
      published: 'Published',
      publishedDate: 'Published Date',
      deadlineDate: 'Deadline Date',
      from: 'From',
      to: 'To',
      anyDate: 'Any date',
      value: 'Est. Value',
      authority: 'Authority',
      sector: 'Industry',
      reference: 'Ref',
      daysLeft: 'days left',
      expired: 'Expired',
      refresh: 'Refresh',
      fetchFromPortal: 'Fetch from Portal',
      page: 'Page',
      of: 'of',
      activeFilters: 'Active filters:',
      clearAll: 'Clear all',
      disclaimer: 'Data Disclaimer',
      disclaimerText: 'This data is aggregated from publicly available sources for informational purposes only. It is NOT an official procurement publication. Always verify information directly with the source portal before making business decisions.',
      portalLinks: 'Official Portal Links',
      scraperStatus: 'Data Sources'
    },
    ar: {
      title: 'المناقصات العامة',
      subtitle: 'بيانات حية من بوابات المناقصات الرسمية في دول الخليج',
      searchPlaceholder: 'البحث في المناقصات...',
      allCountries: 'جميع الدول',
      allStatuses: 'جميع الحالات',
      allSources: 'جميع المصادر',
      open: 'مفتوحة',
      closed: 'مغلقة',
      awarded: 'تمت الترسية',
      results: 'مناقصة',
      noTenders: 'لا توجد مناقصات',
      noTendersDesc: 'انقر على "جلب من البوابة" لجلب البيانات الحية من المصادر الرسمية',
      viewDetails: 'عرض في البوابة',
      deadline: 'الموعد النهائي',
      published: 'تاريخ النشر',
      publishedDate: 'تاريخ النشر',
      deadlineDate: 'تاريخ الموعد النهائي',
      from: 'من',
      to: 'إلى',
      anyDate: 'أي تاريخ',
      value: 'القيمة التقديرية',
      authority: 'الجهة',
      sector: 'الصناعة',
      reference: 'المرجع',
      daysLeft: 'يوم متبقي',
      expired: 'منتهية',
      refresh: 'تحديث',
      fetchFromPortal: 'جلب من البوابة',
      page: 'صفحة',
      of: 'من',
      activeFilters: 'الفلاتر النشطة:',
      clearAll: 'مسح الكل',
      disclaimer: 'إخلاء المسؤولية',
      disclaimerText: 'يتم جمع هذه البيانات من مصادر متاحة للعموم لأغراض إعلامية فقط. هذا ليس إعلان رسمي عن المشتريات. يجب دائماً التحقق من المعلومات مباشرة من البوابة الرسمية قبل اتخاذ قرارات تجارية.',
      portalLinks: 'روابط البوابات الرسمية',
      scraperStatus: 'مصادر البيانات'
    }
  };

  const txt = translations[language];
  
  const statusOptions = [
    { value: 'open', label: txt.open },
    { value: 'closed', label: txt.closed },
    { value: 'awarded', label: txt.awarded }
  ];

  // Extract leads from tender using AI
  const extractLeads = async (tender) => {
    setExtractingLeads(prev => ({ ...prev, [tender._id]: true }));
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Please log in to extract leads');
      }
      
      const response = await fetch(`${BACKEND_URL}/api/intelligence/tenders/${pathId(tender._id)}/extract-leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to extract leads');
      }
      
      if (data.leads && data.leads.length > 0) {
        setCurrentLeads(data.leads);
        setCurrentTenderForLeads(tender);
        setShowLeadsDialog(true);
        toast.success(language === 'ar' 
          ? `تم استخراج ${data.leads.length} فرصة محتملة` 
          : `Extracted ${data.leads.length} potential leads`
        );
      } else {
        toast.info(language === 'ar' 
          ? 'لم يتم العثور على فرص محتملة' 
          : 'No leads found in this tender'
        );
      }
    } catch (error) {
      console.error('Error extracting leads:', error);
      toast.error(language === 'ar' 
        ? 'فشل في استخراج الفرص' 
        : error.message || 'Failed to extract leads'
      );
    } finally {
      setExtractingLeads(prev => ({ ...prev, [tender._id]: false }));
    }
  };

  // Delete tender (admin or the tender's creator)
  const deleteTender = async (tenderId) => {
    // BUG-02: previously gated on `!isAdmin`, silently no-op'ing for the
    // Premium member who created the tender. The backend now authorizes
    // admin-or-owner, so just require a token and surface any 403.
    if (!token) return;
    
    setDeletingTender(prev => ({ ...prev, [tenderId]: true }));
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/tenders/${pathId(tenderId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete tender');
      }
      
      toast.success(language === 'ar' ? 'تم حذف المناقصة بنجاح' : 'Tender deleted successfully');
      fetchTenders(); // Refresh the list
    } catch (error) {
      console.error('Error deleting tender:', error);
      toast.error(error.message);
    } finally {
      setDeletingTender(prev => ({ ...prev, [tenderId]: false }));
    }
  };

  // Save lead to CRM
  const saveToCRM = async (lead) => {
    setSavingLead(prev => ({ ...prev, [lead._id]: true }));
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Please log in to save leads');
      }
      
      const response = await fetch(`${BACKEND_URL}/api/intelligence/tenders/leads/${encodeURIComponent(lead._id)}/save-to-crm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save to CRM');
      }
      
      // Update local state
      setCurrentLeads(prev => prev.map(l => 
        l._id === lead._id ? { ...l, saved_to_crm: true } : l
      ));
      
      toast.success(language === 'ar' 
        ? 'تم حفظ الفرصة في CRM' 
        : 'Lead saved to CRM successfully'
      );
    } catch (error) {
      console.error('Error saving lead to CRM:', error);
      toast.error(language === 'ar' 
        ? 'فشل في حفظ الفرصة' 
        : error.message || 'Failed to save to CRM'
      );
    } finally {
      setSavingLead(prev => ({ ...prev, [lead._id]: false }));
    }
  };

  // Create custom lead
  const createCustomLead = async () => {
    if (!customLeadName.trim()) {
      toast.error(language === 'ar' ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    
    setCreatingCustomLead(true);
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Please log in to create leads');
      }
      
      const response = await fetch(`${BACKEND_URL}/api/intelligence/leads/custom`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_type: customLeadType,
          name: customLeadName.trim(),
          description: customLeadDescription.trim(),
          source_type: 'tender',
          source_id: currentTenderForLeads?._id,
          country: currentTenderForLeads?.country,
          sector: currentTenderForLeads?.sector
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create lead');
      }
      
      // Add the new lead to current leads
      if (data.lead) {
        setCurrentLeads(prev => [...prev, data.lead]);
      }
      
      toast.success(language === 'ar' 
        ? 'تم إنشاء الفرصة بنجاح' 
        : 'Custom lead created successfully'
      );
      
      // Reset form and close dialog
      setCustomLeadName('');
      setCustomLeadDescription('');
      setCustomLeadType('project');
      setShowCustomLeadDialog(false);
    } catch (error) {
      console.error('Error creating custom lead:', error);
      toast.error(language === 'ar' 
        ? 'فشل في إنشاء الفرصة' 
        : error.message || 'Failed to create custom lead'
      );
    } finally {
      setCreatingCustomLead(false);
    }
  };

  const TenderCard = ({ tender }) => {
    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const deadline = formatDeadline(tender.deadline, tender.timezone);
    // NEW-3: derive effective status — an "open"/"under_review" tender whose
    // deadline has already passed must not keep showing as Open.
    const _deadlineDate = tender.deadline ? new Date(tender.deadline) : null;
    const _isExpired = _deadlineDate && !isNaN(_deadlineDate.getTime()) && _deadlineDate < new Date();
    const effectiveStatus = (_isExpired && (tender.status === 'open' || tender.status === 'under_review'))
      ? 'expired'
      : tender.status;
    const StatusIcon = statusIcons[effectiveStatus] || CheckCircle2;
    // Bilingual fields can arrive as { en, ar } objects (sometimes with both
    // values null). Resolve to a string safely — never fall back to the raw
    // object, which crashes React ("Objects are not valid as a React child").
    const pickText = (v) => {
      if (v == null) return '';
      if (typeof v === 'object') return v.en || v.ar || '';
      return v;
    };
    const title = pickText(tender.title) || 'Untitled';
    const authority = pickText(tender.authority) || 'Unknown';
    const sectorText = pickText(tender.sector);
    const canLink = isAdmin || (isPremiumOrEnterprise && tender.created_by === user?.user_id);

    // Left-border accent color keyed to tender status
    const statusAccent = {
      open:         'border-l-emerald-500',
      under_review: 'border-l-blue-500',
      awarded:      'border-l-violet-500',
      closed:       'border-l-gray-300',
      expired:      'border-l-gray-300',
    }[effectiveStatus] || 'border-l-gray-200';

    return (
      <Card
        className={`group relative border border-border/30 bg-card/50 backdrop-blur-sm card-hover card-glow h-full flex flex-col overflow-hidden border-l-4 ${statusAccent}`}
        data-testid={`tender-card-${tender._id}`}
      >
        <div className="card-hover-highlight" />

        <CardContent className="p-6 relative z-10 flex-1 flex flex-col">
          {/* Header */}
          <div className={`flex items-start justify-between gap-4 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center gap-2">
              {tender.ref_id && (
                <Badge className="bg-primary text-primary-foreground font-mono text-xs" data-testid={`tender-ref-id-${tender._id}`}>
                  {tender.ref_id}
                </Badge>
              )}
              <Badge variant="outline" className="flex items-center gap-1 shrink-0">
                <span>{countryFlags[tender.country]}</span>
                <span>{countryNames[language][tender.country]}</span>
              </Badge>
            </div>
            <Badge className={`${statusColors[effectiveStatus]} shrink-0`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {txt[effectiveStatus] || effectiveStatus}
            </Badge>
          </div>

          {/* Reference - fixed height */}
          <div className="h-5 mb-2 overflow-hidden">
            <span className={`text-xs text-muted-foreground/70 font-mono ${isRTL ? 'float-right' : ''}`}>
              {txt.reference}: {tender.reference_no}
            </span>
          </div>

          {/* Title - strict fixed height = 2 lines always */}
          <div className="h-[3.75rem] mb-3 overflow-hidden">
            <h3 className={`text-lg font-bold line-clamp-2 group-hover:text-primary transition-colors leading-tight ${isRTL ? 'text-right' : ''}`}>
              {title}
            </h3>
          </div>

          {/* Authority - fixed height */}
          <div className={`flex items-center gap-2 text-sm text-muted-foreground mb-4 h-5 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <Building className="w-4 h-4 shrink-0 text-primary/50 flex-shrink-0" />
            <span className="truncate">{authority}</span>
          </div>

          {/* Info Grid: Published + Deadline — fixed height so Expired badge never shifts rows below */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className={`p-3 rounded-lg bg-muted/40 border border-border/30 h-[88px] ${isRTL ? 'text-right' : ''}`} data-testid={`tender-card-published-${tender._id}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{txt.publishedDate}</div>
              <div className="font-bold text-sm">
                {tender.published_at
                  ? formatDateInTZ(tender.published_at, tender.timezone, language)
                  : '—'}
              </div>
            </div>
            <div className={`p-3 rounded-lg border h-[88px] ${
              deadline.tier === 'red'   ? 'bg-red-50/60    border-red-200/60    dark:bg-red-950/20 dark:border-red-800/30'
              : deadline.tier === 'amber' ? 'bg-amber-50/60  border-amber-200/60  dark:bg-amber-950/20 dark:border-amber-800/30'
              : deadline.tier === 'green' ? 'bg-emerald-50/60 border-emerald-200/60 dark:bg-emerald-950/20 dark:border-emerald-800/30'
              : deadline.past           ? 'bg-gray-100/60  border-gray-200/60   dark:bg-gray-800/20 dark:border-gray-700/30'
              : 'bg-muted/40 border-border/30'
            } ${isRTL ? 'text-right' : ''}`} data-testid={`tender-card-deadline-${tender._id}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{txt.deadlineDate}</div>
              <div className={`font-bold text-sm ${
                deadline.tier === 'red'   ? 'text-red-600'
                : deadline.tier === 'amber' ? 'text-amber-600'
                : deadline.tier === 'green' ? 'text-emerald-600'
                : deadline.past           ? 'text-gray-400'
                : ''
              }`}>
                {deadline.text}
              </div>
              {deadline.daysLeft !== undefined && deadline.daysLeft > 0 && (
                <Badge
                  variant="outline"
                  className={`mt-1 text-[10px] font-semibold px-1.5 py-0 h-5 ${
                    deadline.tier === 'red'   ? 'border-red-400/50    bg-red-50    text-red-600'
                    : deadline.tier === 'amber' ? 'border-amber-400/50  bg-amber-50  text-amber-600'
                    : 'border-emerald-400/50 bg-emerald-50 text-emerald-600'
                  }`}
                  data-testid={`tender-urgency-badge-${tender._id}`}
                >
                  {deadline.daysLeft} {txt.daysLeft}
                </Badge>
              )}
              {deadline.past && (
                <span className="text-[10px] font-medium text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded mt-1 inline-block">{txt.expired}</span>
              )}
            </div>
          </div>

          {/* Industries Badges - strict fixed height */}
          <div className="h-7 mb-4 overflow-hidden flex flex-wrap gap-1.5 items-start">
            {tender.industries && tender.industries.length > 0 ? (
              tender.industries.slice(0, 3).map((ind, idx) => (
                <Badge key={idx} className="text-xs bg-primary/8 text-primary border border-primary/20 hover:bg-primary/15 font-medium flex-shrink-0">
                  {getIndustryLabel(ind, language)}
                </Badge>
              ))
            ) : sectorText ? (
              <Badge className="text-xs bg-primary/8 text-primary border border-primary/20 font-medium flex-shrink-0">
                {sectorText}
              </Badge>
            ) : null}
          </div>

          {/* Separator */}
          <div className="border-t border-border/40 mb-3" />

          {/* Created by - fixed height */}
          <div className={`flex items-center gap-2 text-xs text-muted-foreground h-4 mb-1.5 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <User className="w-3 h-3 shrink-0" />
            <span className="truncate">{language === 'ar' ? 'أنشئ بواسطة:' : 'Created by:'} {tender.created_by_name || (tender.created_by === 'admin' ? 'Admin' : tender.created_by) || 'System'}</span>
          </div>

          {/* Source - fixed height always (invisible placeholder when absent) */}
          <div className={`flex items-center gap-2 text-xs text-muted-foreground h-4 mb-4 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            {tender.source ? (
              <>
                <Database className="w-3 h-3 shrink-0" />
                <span className="truncate">{language === 'ar' ? 'المصدر:' : 'Source:'} {tender.source}</span>
              </>
            ) : null}
          </div>

          {/* Linked Items Counters */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <LinkedItemsPopover ids={tender.linked_news_ids} type="news" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 border border-border/40 transition-colors ${tender.linked_news_ids?.length ? 'cursor-pointer hover:bg-muted/60 hover:border-border/70' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <Newspaper className="w-3.5 h-3.5" />
                  <span className="text-xs">{language === 'ar' ? 'أخبار' : 'news'}</span>
                </div>
                <p className={`text-xl font-bold ${tender.linked_news_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{tender.linked_news_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
            <LinkedItemsPopover ids={tender.linked_project_ids} type="projects" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 border border-border/40 transition-colors ${tender.linked_project_ids?.length ? 'cursor-pointer hover:bg-muted/60 hover:border-border/70' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <Building2 className="w-3.5 h-3.5" />
                  <span className="text-xs">{language === 'ar' ? 'مشاريع' : 'projects'}</span>
                </div>
                <p className={`text-xl font-bold ${tender.linked_project_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{tender.linked_project_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
            <LinkedItemsPopover ids={tender.linked_company_ids} type="companies" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 border border-border/40 transition-colors ${tender.linked_company_ids?.length ? 'cursor-pointer hover:bg-muted/60 hover:border-border/70' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <Users className="w-3.5 h-3.5" />
                  <span className="text-xs">{language === 'ar' ? 'شركات' : 'companies'}</span>
                </div>
                <p className={`text-xl font-bold ${tender.linked_company_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{tender.linked_company_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
          </div>

          {/* Footer */}
          <div className={`flex items-center justify-between gap-3 pt-4 border-t border-border/50 mt-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
            {tender.visibility === 'private' && (
              <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
                <Lock className="w-3 h-3 mr-1" />
                {language === 'ar' ? 'خاص' : 'Private'}
              </Badge>
            )}
            <EntityCardActions
              itemId={tender._id}
              itemType="tender"
              itemTitle={title}
              shareUrl={tender.visibility === 'private' && tender.share_token
                ? `${window.location.origin}/shared/tender/${tender.share_token}`
                : null
              }
              isAdmin={isAdmin}
              canEdit={isAdmin}
              canLink={canLink}
              canExtractLeads={isAdmin}
              onEdit={() => { setEditingTender(tender); setShowAdminTenderDialog(true); }}
              onDelete={(isAdmin || (tender.created_by && user?.id && tender.created_by === user.id))
                ? () => setTenderToDelete(tender)
                : undefined}
              onLink={() => setShowLinkDialog(true)}
              onExtractLeads={() => extractLeads(tender)}
              isExtractingLeads={extractingLeads[tender._id]}
              isFavorite={isFavorite(tender._id)}
              onFavoriteToggle={(isFav) => handleFavoriteToggle(tender._id, isFav)}
              canSaveAsLead={onb.on}
              onSaveAsLead={() => handleAddTenderToCrm(tender)}
              isSavingLead={crmSavingId === tender._id}
              savedAsLead={crmSavedIds.has(tender._id)}
              saveAsLeadLabel={onb.on && language !== 'ar' ? onboardingCopy.en.addToCrm.label : undefined}
              savedAsLeadLabel={onb.on && language !== 'ar' ? onboardingCopy.en.addToCrm.inCrm : undefined}
              actionsOnbTarget={onb.on ? 'card-actions' : undefined}
              token={token}
              language={language}
              isRTL={isRTL}
              primaryAction={(tender.source_url && !String(tender.source_url).startsWith('manual://'))
                ? () => window.open(tender.source_url, '_blank', 'noopener,noreferrer')
                : undefined}
              primaryLabel={(tender.source_url && !String(tender.source_url).startsWith('manual://')) ? txt.viewDetails : undefined}
              primaryIcon={ExternalLink}
            />
          </div>
        </CardContent>
        
        {/* Manage Links Dialog */}
        <ManageLinksDialog
          open={showLinkDialog}
          onOpenChange={setShowLinkDialog}
          fromType="tenders"
          fromId={tender._id}
          fromTitle={title}
          currentLinks={{
            news:      tender.linked_news_ids     || [],
            projects:  tender.linked_project_ids  || [],
            companies: tender.linked_company_ids  || [],
          }}
          token={token}
          onSuccess={fetchTenders}
        />
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="tenders-page">
      {/* Active background tender job indicator */}
      {isAdmin && activeTenderJob && (activeTenderJob.status === 'running' || activeTenderJob.status === 'queued') && (
        <div
          className="border border-emerald-500/40 bg-emerald-500/5 rounded-lg p-4 flex items-center gap-3"
          data-testid="tender-job-banner"
        >
          <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
          <div className="flex-1">
            <div className="font-medium text-sm">
              {language === 'ar' ? 'تحديث المناقصات قيد التشغيل' : 'Tender refresh running in background'}
            </div>
            <div className="text-xs text-muted-foreground">
              {activeTenderJob.current_source
                ? (language === 'ar'
                    ? `${activeTenderJob.current_source.name} (${activeTenderJob.current_source.index}/${activeTenderJob.current_source.total})`
                    : `${activeTenderJob.current_source.name} (${activeTenderJob.current_source.index}/${activeTenderJob.current_source.total})`)
                : (language === 'ar' ? 'جارٍ بدء التشغيل…' : 'Starting…')}
              {' • '}
              {(activeTenderJob.totals?.created || 0)} {language === 'ar' ? 'جديد' : 'new'}
              {' • '}
              {(activeTenderJob.totals?.translated || 0)} {language === 'ar' ? 'مترجم' : 'translated'}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                await fetch(`${BACKEND_URL}/api/intelligence/tenders/ingest-job/${activeTenderJob._id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` },
                });
                toast.info(language === 'ar' ? 'تم إلغاء التحديث' : 'Cancellation requested');
              } catch {
                toast.error('Cancel failed');
              }
            }}
            data-testid="tender-job-cancel-btn"
          >
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
        </div>
      )}

      {/* Scraper health panel removed — available in Admin → Tender Sources */}

      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 shadow-lg">
              <FileText className="w-7 h-7 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground mt-2">{txt.subtitle}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          {/* Row 1: Main action buttons */}
          <div className="flex items-center gap-2">
            {/* Create Tender Button — Premium & Enterprise */}
            {(isAdmin || isPremiumOrEnterprise) && (
              <Button 
                onClick={() => {
                  setEditingTender(null);
                  setShowAdminTenderDialog(true);
                }}
                className="flex items-center gap-2"
                data-testid="create-tender-btn"
              >
                <Plus className="w-4 h-4" />
                {language === 'ar' ? 'إنشاء مناقصة' : 'Create Tender'}
              </Button>
            )}
            
            {/* Admin-only Retrieval Button */}
            {isAdmin && (
              <Popover open={retrievalOpen} onOpenChange={setRetrievalOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="default"
                    className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700"
                    data-testid="retrieval-btn"
                  >
                    <Database className="w-4 h-4" />
                    {language === 'ar' ? 'استرجاع' : 'Retrieval'}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[22rem] p-0" align="end">
                  <div className="p-3 border-b">
                    <h4 className="font-medium text-sm">
                      {language === 'ar' ? 'مصادر المناقصات' : 'Tender Sources'}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === 'ar'
                        ? 'يقرأ الخادم أبوظبي والبحرين وأشغال. تُجمع البوابات الأخرى بإضافة Mid2Bid Collector.'
                        : 'The server reads Abu Dhabi, Bahrain and Ashghal. Every other portal is collected with the Mid2Bid Collector add-on.'}
                    </p>
                  </div>
                  <div className="p-2 space-y-2">
                    <Button
                      variant="default"
                      className="w-full justify-center gap-2 bg-gradient-to-r from-emerald-600 to-blue-600"
                      onClick={() => { triggerScrape('server'); setRetrievalOpen(false); }}
                      disabled={Object.values(scraping).some(v => v)}
                      data-testid="scrape-server-btn"
                    >
                      {Object.values(scraping).some(v => v) ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {language === 'ar' ? 'تحديث مصادر الخادم' : 'Refresh server sources'}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-center"
                      onClick={() => { setRetrievalOpen(false); navigate('/dashboard/admin/tender-sources'); }}
                      data-testid="open-tender-sources-btn"
                    >
                      {language === 'ar' ? 'المُجمِّع وكل المصادر' : 'Collector and all sources'}
                    </Button>
                  </div>
              </PopoverContent>
            </Popover>
          )}
          </div>
          
          {/* Row 2: View controls */}
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'card' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('card')}
              data-testid="view-mode-card"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('table')}
              data-testid="view-mode-table"
            >
              <List className="w-4 h-4" />
            </Button>
            {isAdmin && (
            <Button 
              variant="outline" 
              onClick={() => fetchTenders(false, true)}
              disabled={loading}
              className="flex items-center gap-2"
              data-testid="refresh-tenders-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {txt.refresh}
            </Button>
            )}
          </div>
        </div>
      </div>


      {/* Filters */}
      <Card className="border-0 bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className={`flex flex-col gap-3 ${isRTL ? 'items-end' : ''}`}>
            {/* Row 1: Search + primary filters */}
            <div data-onb="tender-filters" className={`m2b-mfilters flex flex-col md:flex-row gap-3 flex-wrap ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="m2b-keep relative flex-1 min-w-[200px]">
              <Search className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={txt.searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} h-11`}
                data-testid="tenders-search-input"
              />
            </div>
            {/* WO-40 — phones: filters fold away behind this button (CSS in index.css, .m2b-mfilters) */}
            <label className="m2b-keep m2b-ftoggle md:hidden flex items-center justify-between h-11 px-4 border rounded-lg bg-background text-sm font-medium cursor-pointer select-none" data-testid="tenders-filters-toggle">
              <input type="checkbox" className="sr-only" aria-label={language === 'ar' ? 'إظهار عوامل التصفية' : 'Show filters'} />
              <span>{language === 'ar' ? 'عوامل التصفية' : 'Filters'}</span>
              <span className="m2b-ftoggle-arrow" aria-hidden="true">▾</span>
            </label>
            
            {/* Multi-select Country Filter */}
            <MultiSelectFilter
              label={txt.allCountries}
              icon={Globe}
              options={Object.keys(countryFlags).map(code => ({
                value: code,
                label: countryNames[language][code],
                icon: countryFlags[code],
                locked: isCountryLocked(code)
              }))}
              onLockedClick={handleLockedCountry}
              selected={selectedCountries}
              onChange={(values) => { setSelectedCountries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="tenders-country-filter"
            />
            
            {/* Multi-select Source Filter */}
            <MultiSelectFilter
              label={txt.allSources}
              icon={Rss}
              options={ALL_TENDER_SOURCES.map(src => ({
                value: src.value,
                label: src.label,
                icon: countryFlags[src.country],
                comingSoon: src.comingSoon
              }))}
              selected={selectedSources}
              onChange={(values) => { setSelectedSources(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="tenders-source-filter"
            />
            
            {/* Multi-select Industry Filter (grouped by category) */}
            <IndustryMultiSelectFilter
              label={language === 'ar' ? 'جميع الصناعات' : 'All Industries'}
              icon={Factory}
              selected={selectedIndustries}
              onChange={(values) => { setSelectedIndustries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="tenders-industry-filter"
            />

            {/* Filter by linked Project */}
            <LinkedEntityFilter
              entityType="projects"
              selected={linkedProjectFilter}
              onChange={(val) => { setLinkedProjectFilter(val); setPage(1); }}
              language={language}
              token={token}
              testId="tenders-project-filter"
            />

            {/* Filter by linked Company */}
            <LinkedEntityFilter
              entityType="companies"
              selected={linkedCompanyFilter}
              onChange={(val) => { setLinkedCompanyFilter(val); setPage(1); }}
              language={language}
              token={token}
              testId="tenders-company-filter"
            />
            
            {/* (moved to row 2) Status + Published Date */}
            </div>

            {/* Row 2: Status + Published Date + Deadline (left) + spacer + Search + Favorites (right) */}
            <div className={`m2b-mfilters-more flex flex-col md:flex-row gap-3 flex-wrap items-stretch md:items-center ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            {/* Multi-select Status Filter */}
            <MultiSelectFilter
              label={txt.allStatuses}
              icon={Filter}
              options={statusOptions}
              selected={selectedStatuses}
              onChange={(values) => { setSelectedStatuses(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="tenders-status-filter"
            />

            {/* Published Date Filter */}
            <DateFilter
              label={txt.publishedDate}
              selectedPreset={datePreset}
              onPresetChange={(preset) => { setDatePreset(preset); setPage(1); }}
              dateRange={dateRange}
              onDateRangeChange={(range) => { setDateRange(range); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="tenders-published-date-filter"
            />

            {/* Deadline Date Filter (allows future dates) */}
            <DateFilter
              label={txt.deadlineDate}
              selectedPreset={deadlinePreset}
              onPresetChange={(preset) => { setDeadlinePreset(preset); setPage(1); }}
              dateRange={deadlineRange}
              onDateRangeChange={(range) => { setDeadlineRange(range); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="tenders-deadline-date-filter"
              allowFuture
              presets={deadlinePresets}
            />

            {/* Spacer pushes action buttons to the right corner */}
            <div className="flex-1" />

            {/* Create Alert Button - shows when filters are active */}
            {hasActiveFilters && user?.plan_id !== 'free' && (
              <Button 
                type="button"
                variant="outline" 
                className="h-11 border-amber-500/50 text-amber-600 hover:bg-amber-500/10 hover:text-amber-700"
                onClick={openCreateAlertDialog}
                data-testid="create-alert-from-filters-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'إنشاء تنبيه' : 'Create Alert'}
              </Button>
            )}

            {/* Favorites Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-background">
              <Heart className={`w-4 h-4 ${showFavoritesOnly ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
              <span className="text-sm whitespace-nowrap">
                {language === 'ar' ? 'المفضلة فقط' : 'Favorites only'}
              </span>
              <Switch
                checked={showFavoritesOnly}
                onCheckedChange={setShowFavoritesOnly}
                data-testid="tenders-favorites-toggle"
              />
            </div>

            <Button type="submit" className="m2b-keep bg-primary hover:bg-primary/90 h-11" data-testid="tenders-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'بحث' : 'Search'}
            </Button>
            </div>
          </form>
          
          {/* Active filters display */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50 flex-wrap">
              <span className="text-sm text-muted-foreground">{txt.activeFilters}</span>
              {selectedCountries.map(code => (
                <Badge key={code} variant="secondary" className="gap-1">
                  {countryFlags[code]} {countryNames[language][code]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCountries(prev => prev.filter(c => c !== code))} />
                </Badge>
              ))}
              {selectedStatuses.map(status => (
                <Badge key={status} variant="secondary" className="gap-1">
                  <Filter className="w-3 h-3" /> {txt[status]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedStatuses(prev => prev.filter(s => s !== status))} />
                </Badge>
              ))}
              {selectedSources.map(src => (
                <Badge key={src} variant="secondary" className="gap-1">
                  <Rss className="w-3 h-3" /> {src}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedSources(prev => prev.filter(s => s !== src))} />
                </Badge>
              ))}
              {datePreset !== 'any' && (
                <Badge variant="secondary" className="gap-1">
                  <CalendarDays className="w-3 h-3" /> 
                  {datePreset === 'custom' && dateRange.from 
                    ? `${dateRange.from.toLocaleDateString()} - ${dateRange.to?.toLocaleDateString() || (language === 'ar' ? 'الآن' : 'Now')}`
                    : datePresets.find(p => p.id === datePreset)?.[language === 'ar' ? 'ar' : 'en']
                  }
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setDatePreset('any'); setDateRange({ from: null, to: null }); }} />
                </Badge>
              )}
              {linkedProjectFilter.map(f => (
                <Badge key={f._id} variant="secondary" className="gap-1 border-primary/30 bg-primary/10 text-primary">
                  <Building2 className="w-3 h-3" /> {f.name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setLinkedProjectFilter(prev => prev.filter(x => x._id !== f._id))} />
                </Badge>
              ))}
              {linkedCompanyFilter.map(f => (
                <Badge key={f._id} variant="secondary" className="gap-1 border-primary/30 bg-primary/10 text-primary">
                  <Building2 className="w-3 h-3" /> {f.name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setLinkedCompanyFilter(prev => prev.filter(x => x._id !== f._id))} />
                </Badge>
              ))}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7">
                {txt.clearAll}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legal Disclaimer Banner - Moved below filters */}
      <Alert className="border-amber-500/30 bg-amber-500/5">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertTitle className="text-amber-600">{txt.disclaimer}</AlertTitle>
        <AlertDescription className="text-sm text-muted-foreground">
          {txt.disclaimerText}
        </AlertDescription>
      </Alert>

      {/* Sources info panel moved to Admin → Tender Sources */}


      {/* Results count */}
      {!loading && (
        <div className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
          {language === 'ar' 
            ? `عرض ${displayedTenders.length} من ${total} مناقصة${showFavoritesOnly ? ' (المفضلة)' : ''}` 
            : `Showing ${displayedTenders.length} of ${total} tenders${showFavoritesOnly ? ' (Favorites)' : ''}`
          }
        </div>
      )}

      {/* Linked filter banner */}
      {isFiltered && !loading && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm">
          <span className="font-medium text-primary">
            Showing {filterIds.length} linked tender{filterIds.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => navigate('/dashboard/tenders')}
            className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            View all tenders <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-0 bg-card/50">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-16" />
                </div>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-3/4" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-0 bg-destructive/10">
          <CardContent className="p-8 text-center">
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchTenders} variant="outline" className="mt-4">
              {language === 'ar' ? 'إعادة المحاولة' : 'Try Again'}
            </Button>
          </CardContent>
        </Card>
      ) : displayedTenders.length === 0 ? (
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            {countryGate ? (
              <>
                <h3 className="text-xl font-semibold mb-2">{language === 'ar' ? 'سوق مقفل' : 'Locked market'}</h3>
                <p className="text-muted-foreground mb-6">{countryGate.message}</p>
                <Button data-testid="country-gate-upgrade-btn" onClick={() => { window.location.href = countryGate.upgrade_url || '/dashboard/settings?tab=plans'; }}>
                  {language === 'ar' ? 'ترقية الخطة' : 'Upgrade plan'}
                </Button>
              </>
            ) : (
            <OnboardingEmptyState kind="noResults" variant="inline" when={hasActiveFilters || !!search} onClear={() => { clearAllFilters(); setSearch(''); setSearchInput(''); }}>
            <>
            <h3 className="text-xl font-semibold mb-2">{txt.noTenders}</h3>
            <p className="text-muted-foreground mb-6">{txt.noTendersDesc}</p>
            </>
            </OnboardingEmptyState>
            )}
            
            {/* Portal Links */}
            <div className="max-w-2xl mx-auto">
              <h4 className="text-sm font-medium mb-4">{txt.portalLinks}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(PORTAL_URLS).map(([country, portals]) => (
                  portals.map((portal, idx) => (
                    <Button
                      key={`${country}-${idx}`}
                      variant="outline"
                      size="sm"
                      className="justify-start gap-2"
                      disabled={!safeUrl(portal.url)}
                      onClick={() => { const u = safeUrl(portal.url); if (u) window.open(u, '_blank'); }}
                    >
                      <span>{countryFlags[country]}</span>
                      <span className="truncate">{portal.name}</span>
                      <ExternalLink className="w-3 h-3 ml-auto" />
                    </Button>
                  ))
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {displayedTenders.map((tender) => (
            <div
              key={tender._id}
              ref={tender._id === deepLinkId ? deepLinkRef : null}
              className={tender._id === deepLinkId ? 'ring-2 ring-primary ring-offset-2 rounded-xl' : ''}
            >
              <TenderCard tender={tender} />
            </div>
          ))}
        </div>
      ) : (
        <Card className="border-0 bg-card/50 backdrop-blur-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{txt.reference}</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>{txt.authority}</TableHead>
                <TableHead>{language === 'ar' ? 'الدولة' : 'Country'}</TableHead>
                <TableHead>{txt.publishedDate}</TableHead>
                <TableHead>{txt.deadlineDate}</TableHead>
                <TableHead>{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedTenders.map((tender) => {
                const deadline = formatDeadline(tender.deadline, tender.timezone);
                const title = tender.title?.[language] || tender.title?.en || tender.title?.ar || 'Untitled';
                const authority = tender.authority?.[language] || tender.authority?.en || tender.authority?.ar || 'Unknown';
                const pubDateStr = tender.published_at
                  ? formatDateInTZ(tender.published_at, tender.timezone, language)
                  : '—';
                return (
                  <TableRow key={tender._id}>
                    <TableCell className="font-mono text-xs">{tender.reference_no}</TableCell>
                    <TableCell className="max-w-[200px] truncate font-medium">{title}</TableCell>
                    <TableCell className="max-w-[150px] truncate text-sm">{authority}</TableCell>
                    <TableCell>
                      <span>{countryFlags[tender.country]}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {pubDateStr}
                    </TableCell>
                    <TableCell className={
                      deadline.tier === 'red' ? 'text-red-600'
                      : deadline.tier === 'amber' ? 'text-amber-600'
                      : deadline.tier === 'green' ? 'text-emerald-600'
                      : deadline.past ? 'text-gray-500'
                      : ''
                    }>
                      <div className="flex items-center gap-2">
                        <span>{deadline.text}</span>
                        {deadline.daysLeft !== undefined && deadline.daysLeft > 0 && (
                          <Badge
                            variant="outline"
                            className={`text-[10px] font-semibold px-1.5 py-0 h-5 ${
                              deadline.tier === 'red' ? 'border-red-500/50 bg-red-500/10 text-red-600'
                              : deadline.tier === 'amber' ? 'border-amber-500/50 bg-amber-500/10 text-amber-600'
                              : 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600'
                            }`}
                            data-testid={`tender-urgency-badge-row-${tender._id}`}
                          >
                            {deadline.daysLeft}d
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[tender.status]}>
                        {txt[tender.status] || tender.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        disabled={!safeUrl(tender.source_url)}
                        onClick={() => { const u = safeUrl(tender.source_url); if (u) window.open(u, '_blank'); }}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Load More Button */}
      {hasMore && displayedTenders.length > 0 && (
        <div className="flex justify-center pt-8">
          <Button
            variant="outline"
            size="lg"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-w-[200px]"
            data-testid="load-more-tenders"
          >
            {loadingMore ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'تحميل المزيد من المناقصات' : 'Load More Tenders'}
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* No more items message */}
      {!hasMore && displayedTenders.length > 0 && (
        <div className="text-center pt-8 text-muted-foreground">
          <p>{language === 'ar' ? 'لا يوجد المزيد من المناقصات' : 'No more tenders to load'}</p>
        </div>
      )}

      {/* Leads Dialog */}
      <Dialog open={showLeadsDialog} onOpenChange={setShowLeadsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              {language === 'ar' ? 'الفرص المستخرجة' : 'Extracted Leads'}
            </DialogTitle>
            <DialogDescription>
              {currentTenderForLeads && (
                <span className="line-clamp-2">
                  {language === 'ar' ? 'من المناقصة: ' : 'From tender: '}
                  {currentTenderForLeads.title?.[language] || currentTenderForLeads.title?.en || 'Untitled'}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {currentLeads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Zap className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>{language === 'ar' ? 'لم يتم العثور على فرص' : 'No leads found'}</p>
              </div>
            ) : (
              currentLeads.map((lead) => (
                <Card key={lead._id} className={`border ${lead.saved_to_crm ? 'border-green-500/30 bg-green-500/5' : 'border-border'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {lead.lead_type === 'project' ? (
                            <Briefcase className="w-4 h-4 text-blue-500" />
                          ) : (
                            <Users className="w-4 h-4 text-purple-500" />
                          )}
                          <Badge variant={lead.lead_type === 'project' ? 'default' : 'secondary'}>
                            {lead.lead_type === 'project' 
                              ? (language === 'ar' ? 'مشروع' : 'Project')
                              : (language === 'ar' ? 'شركة' : 'Company')
                            }
                          </Badge>
                          {lead.saved_to_crm && (
                            <Badge variant="outline" className="text-green-600 border-green-500/30">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              {language === 'ar' ? 'محفوظ في CRM' : 'Saved to CRM'}
                            </Badge>
                          )}
                        </div>
                        
                        <h4 className="font-semibold mb-1">
                          {lead.project_name || lead.company_name || lead.title}
                        </h4>
                        
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {lead.description}
                        </p>
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {lead.country && (
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {countryFlags[lead.country]} {countryNames[language][lead.country]}
                            </span>
                          )}
                          {lead.sector && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {lead.sector}
                            </span>
                          )}
                          {lead.estimated_value && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {formatCurrency(lead.estimated_value, lead.currency)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        variant={lead.saved_to_crm ? 'outline' : 'default'}
                        size="sm"
                        disabled={lead.saved_to_crm || savingLead[lead._id]}
                        onClick={() => saveToCRM(lead)}
                        data-testid={`save-lead-${lead._id}`}
                      >
                        {savingLead[lead._id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : lead.saved_to_crm ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            {language === 'ar' ? 'محفوظ' : 'Saved'}
                          </>
                        ) : (
                          <>
                            <Briefcase className="w-4 h-4 mr-1" />
                            {language === 'ar' ? 'حفظ في CRM' : 'Save to CRM'}
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          
          <div className="flex justify-between items-center pt-4 border-t mt-4">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCustomLeadDialog(true)}
                className="flex items-center gap-2"
                data-testid="add-custom-lead-btn"
              >
                <UserPlus className="w-4 h-4" />
                {language === 'ar' ? 'إضافة فرصة مخصصة' : 'Add Custom Lead'}
              </Button>
              {currentLeads.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {currentLeads.filter(l => l.saved_to_crm).length} / {currentLeads.length} {language === 'ar' ? 'محفوظ' : 'saved'}
                </span>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowLeadsDialog(false)}>
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Lead Dialog */}
      <Dialog open={showCustomLeadDialog} onOpenChange={setShowCustomLeadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              {language === 'ar' ? 'إضافة فرصة مخصصة' : 'Add Custom Lead'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'أدخل تفاصيل الفرصة المخصصة من هذه المناقصة'
                : 'Enter the details for a custom lead from this tender'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Lead Type Selection */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'نوع الفرصة' : 'Lead Type'}</Label>
              <RadioGroup 
                value={customLeadType} 
                onValueChange={setCustomLeadType}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="project" id="lead-type-project" />
                  <Label htmlFor="lead-type-project" className="flex items-center gap-2 cursor-pointer">
                    <Briefcase className="w-4 h-4 text-blue-500" />
                    {language === 'ar' ? 'مشروع' : 'Project'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="company" id="lead-type-company" />
                  <Label htmlFor="lead-type-company" className="flex items-center gap-2 cursor-pointer">
                    <Users className="w-4 h-4 text-purple-500" />
                    {language === 'ar' ? 'شركة' : 'Company'}
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {/* Lead Name */}
            <div className="space-y-2">
              <Label htmlFor="custom-lead-name">
                {customLeadType === 'project' 
                  ? (language === 'ar' ? 'اسم المشروع' : 'Project Name')
                  : (language === 'ar' ? 'اسم الشركة' : 'Company Name')
                }
              </Label>
              <Input
                id="custom-lead-name"
                placeholder={customLeadType === 'project' 
                  ? (language === 'ar' ? 'أدخل اسم المشروع...' : 'Enter project name...')
                  : (language === 'ar' ? 'أدخل اسم الشركة...' : 'Enter company name...')
                }
                value={customLeadName}
                onChange={(e) => setCustomLeadName(e.target.value)}
                data-testid="custom-lead-name-input"
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="custom-lead-description">
                {language === 'ar' ? 'الوصف (اختياري)' : 'Description (optional)'}
              </Label>
              <Textarea
                id="custom-lead-description"
                placeholder={language === 'ar' ? 'أدخل وصفًا للفرصة...' : 'Enter a description for this lead...'}
                value={customLeadDescription}
                onChange={(e) => setCustomLeadDescription(e.target.value)}
                rows={3}
                data-testid="custom-lead-description-input"
              />
            </div>
            
            {/* Source Info */}
            {currentTenderForLeads && (
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="text-muted-foreground mb-1">
                  {language === 'ar' ? 'المصدر:' : 'Source:'}
                </p>
                <p className="font-medium line-clamp-2">
                  {currentTenderForLeads.title?.[language] || currentTenderForLeads.title?.en || 'Untitled'}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCustomLeadDialog(false);
                setCustomLeadName('');
                setCustomLeadDescription('');
              }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={createCustomLead}
              disabled={!customLeadName.trim() || creatingCustomLead}
              data-testid="create-custom-lead-btn"
            >
              {creatingCustomLead ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'إنشاء الفرصة' : 'Create Lead'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Admin Tender Dialog */}
      {(isAdmin || isPremiumOrEnterprise) && (
        <AdminTenderDialog
          open={showAdminTenderDialog}
          onOpenChange={(open) => {
            setShowAdminTenderDialog(open);
            if (!open) setEditingTender(null);
          }}
          tender={editingTender}
          token={token}
          language={language}
          userPlan={user?.plan_id || 'free'}
          onSuccess={() => {
            fetchTenders();
          }}
          onPlanGate={(pg) => planGate.show(pg)}
        />
      )}
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      
      {/* Delete Tender Confirmation Dialog */}
      <AlertDialog open={!!tenderToDelete} onOpenChange={(open) => !open && setTenderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{language === 'ar' ? 'حذف المناقصة' : 'Delete Tender'}</AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'ar' 
                ? 'هل أنت متأكد من حذف هذه المناقصة؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to delete this tender? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {tenderToDelete && (
            <div className="my-4 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium line-clamp-2">{pickTenderText(tenderToDelete.title) || pickTenderText(tenderToDelete.title_en) || pickTenderText(tenderToDelete.title_ar) || 'Untitled'}</p>
              <p className="text-xs text-muted-foreground mt-1">{pickTenderText(tenderToDelete.reference_no)}</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'ar' ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (tenderToDelete) {
                  deleteTender(tenderToDelete._id);
                  setTenderToDelete(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {language === 'ar' ? 'حذف' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Create Alert from Filters Dialog */}
      <Dialog open={showCreateAlertDialog} onOpenChange={setShowCreateAlertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              {language === 'ar' ? 'إنشاء تنبيه' : 'Create Alert'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'سيتم إعلامك عند نشر مناقصات جديدة تطابق هذه المعايير'
                : 'You\'ll be notified when new tenders match these criteria'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Filters Summary */}
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                {language === 'ar' ? 'الفلاتر المحددة' : 'Selected Filters'}
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {selectedCountries.map(code => (
                  <Badge key={code} variant="outline" className="text-xs">
                    {countryFlags[code]} {countryNames[language][code]}
                  </Badge>
                ))}
                {selectedIndustries.map(id => {
                  const ind = STANDARD_INDUSTRIES.find(i => i.id === id);
                  return (
                    <Badge key={id} variant="outline" className="text-xs">
                      {ind ? (language === 'ar' ? ind.ar : ind.en) : id}
                    </Badge>
                  );
                })}
                {selectedSources.map(src => (
                  <Badge key={src} variant="outline" className="text-xs">
                    {src}
                  </Badge>
                ))}
                {search && (
                  <Badge variant="outline" className="text-xs">
                    "{search}"
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Alert Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'اسم التنبيه' : 'Alert Name'}</Label>
              <Input
                value={alertName}
                onChange={(e) => setAlertName(e.target.value)}
                placeholder={language === 'ar' ? 'مثال: مناقصات البناء السعودية' : 'e.g., Saudi Construction Tenders'}
                data-testid="alert-name-input"
              />
            </div>
            
            {/* Frequency */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'تكرار الإشعارات' : 'Notification Frequency'}</Label>
              <Select value={alertFrequency} onValueChange={setAlertFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="realtime">
                    {language === 'ar' ? 'فوري (عند النشر)' : 'Real-time (Immediate)'}
                  </SelectItem>
                  <SelectItem value="daily">
                    {language === 'ar' ? 'ملخص يومي' : 'Daily Digest'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Notification Channels */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'قنوات الإشعار' : 'Notification Channels'}</Label>
              <div className="flex flex-wrap gap-2">
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    alertChannels.includes('in_app') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setAlertChannels(prev => 
                    prev.includes('in_app') ? prev.filter(c => c !== 'in_app') : [...prev, 'in_app']
                  )}
                >
                  <Bell className="w-4 h-4" />
                  <span className="text-sm">{language === 'ar' ? 'داخل التطبيق' : 'In-App'}</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    alertChannels.includes('email') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setAlertChannels(prev => 
                    prev.includes('email') ? prev.filter(c => c !== 'email') : [...prev, 'email']
                  )}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                  <span className="text-sm">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    alertChannels.includes('push') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setAlertChannels(prev => 
                    prev.includes('push') ? prev.filter(c => c !== 'push') : [...prev, 'push']
                  )}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="12" y1="18" x2="12" y2="18" strokeLinecap="round" />
                  </svg>
                  <span className="text-sm">{language === 'ar' ? 'إشعارات المتصفح' : 'Browser Push'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAlertDialog(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateAlertFromFilters} 
              disabled={savingAlert || !alertName.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="save-alert-btn"
            >
              {savingAlert ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'إنشاء التنبيه' : 'Create Alert'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
